// Copyright (c) Ping. All rights reserved.

import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
} from 'react';

/**
 * Tiny in-tree client-side router. We deliberately do not pull in
 * `react-router` / `tanstack-router` for this surface — the route set
 * is closed (10 entries), the API surface is `push` + `openModal` +
 * `closeModal`, and this file is the single source of truth for
 * deep-link parsing. DESK-WIRE re-uses the same context behind a
 * memory-based history shim.
 */
export type WebRoute =
  | '/business-directory'
  | '/chat'
  | '/activity'
  | '/contacts'
  | '/calls'
  | '/tasks'
  | '/notes'
  | '/leaderboard'
  | '/settings'
  | '/status';

export type ModalKind =
  | { readonly kind: 'none' }
  | { readonly kind: 'settings' }
  | { readonly kind: 'status' }
  // Global search modal. Opens from the TopBar search input, from
  // Cmd/Ctrl+K, or programmatically via `router.openModal('search')`.
  // The host (apps/web/src/shell/hosts/GlobalSearchModalHost.tsx)
  // owns the result-section adapters; the modal itself is
  // presentation-only.
  | { readonly kind: 'search' };

export interface RouterApi {
  readonly route: WebRoute;
  readonly modal: ModalKind;
  push(next: WebRoute): void;
  openModal(kind: Exclude<ModalKind, { kind: 'none' }>['kind']): void;
  closeModal(): void;
}

export const KNOWN_ROUTES: readonly WebRoute[] = [
  '/business-directory',
  '/chat',
  '/activity',
  '/contacts',
  '/calls',
  '/tasks',
  '/notes',
  '/leaderboard',
  '/settings',
  '/status',
] as const;

const RouterContext = createContext<RouterApi | null>(null);

interface RouterState {
  readonly route: WebRoute;
  readonly modal: ModalKind;
}

type RouterAction =
  | { type: 'push'; route: WebRoute }
  | { type: 'pop'; route: WebRoute }
  | { type: 'openModal'; modal: Exclude<ModalKind, { kind: 'none' }> }
  | { type: 'closeModal' };

function reducer(state: RouterState, action: RouterAction): RouterState {
  switch (action.type) {
    case 'push':
      if (state.route === action.route) {
        return state;
      }
      return { ...state, route: action.route };
    case 'pop':
      return { ...state, route: action.route };
    case 'openModal':
      return { ...state, modal: action.modal };
    case 'closeModal':
      if (state.modal.kind === 'none') {
        return state;
      }
      return { ...state, modal: { kind: 'none' } };
    default:
      return state;
  }
}

export function isWebRoute(input: string): input is WebRoute {
  return (KNOWN_ROUTES as readonly string[]).includes(input);
}

function readInitialRoute(initial: WebRoute | undefined): WebRoute {
  if (initial !== undefined) {
    return initial;
  }
  if (typeof window === 'undefined') {
    return '/chat';
  }
  const path = window.location.pathname;
  return isWebRoute(path) ? path : '/chat';
}

export interface RouterProviderProps {
  readonly initial?: WebRoute;
  readonly children: ReactNode;
}

export function RouterProvider({ initial, children }: RouterProviderProps) {
  const [state, dispatch] = useReducer(reducer, undefined, () => ({
    route: readInitialRoute(initial),
    modal: { kind: 'none' } as ModalKind,
  }));

  // Track history-stack depth contributed by modal opens so we can
  // synchronously balance pushes/pops on close. Refs avoid extra
  // renders.
  const modalDepthRef = useRef(0);

  // Capture the bootstrap route in a ref so the mount-only effect
  // can seed `history.state` without invalidating itself when the
  // route later changes (every push() would otherwise re-run this
  // effect and stomp the entry it just created).
  const initialRouteRef = useRef(state.route);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    if (window.history.state === null) {
      window.history.replaceState({ route: initialRouteRef.current }, '', initialRouteRef.current);
    }
    const onPopState = (event: PopStateEvent): void => {
      const popped = event.state as { route?: unknown } | null;
      const next =
        popped && typeof popped.route === 'string' && isWebRoute(popped.route)
          ? popped.route
          : isWebRoute(window.location.pathname)
            ? window.location.pathname
            : '/chat';
      if (modalDepthRef.current > 0) {
        modalDepthRef.current = Math.max(0, modalDepthRef.current - 1);
        dispatch({ type: 'closeModal' });
        return;
      }
      dispatch({ type: 'pop', route: next });
    };
    window.addEventListener('popstate', onPopState);
    return () => {
      window.removeEventListener('popstate', onPopState);
    };
  }, []);

  const push = useCallback((next: WebRoute): void => {
    if (typeof window !== 'undefined') {
      window.history.pushState({ route: next }, '', next);
    }
    dispatch({ type: 'push', route: next });
  }, []);

  const openModal = useCallback((kind: Exclude<ModalKind, { kind: 'none' }>['kind']): void => {
    if (typeof window !== 'undefined') {
      // Synthetic history entry so browser-back closes the modal
      // before changing the route. We track the depth so popstate
      // unwinds the right number of entries.
      modalDepthRef.current += 1;
      window.history.pushState({ modal: kind }, '', window.location.pathname);
    }
    dispatch({ type: 'openModal', modal: { kind } });
  }, []);

  const closeModal = useCallback((): void => {
    if (typeof window !== 'undefined' && modalDepthRef.current > 0) {
      modalDepthRef.current = Math.max(0, modalDepthRef.current - 1);
      window.history.back();
    }
    dispatch({ type: 'closeModal' });
  }, []);

  const api = useMemo<RouterApi>(
    () => ({
      route: state.route,
      modal: state.modal,
      push,
      openModal,
      closeModal,
    }),
    [state.route, state.modal, push, openModal, closeModal],
  );

  return <RouterContext.Provider value={api}>{children}</RouterContext.Provider>;
}

export function useRouter(): RouterApi {
  const value = useContext(RouterContext);
  if (value === null) {
    throw new Error('useRouter must be used inside <RouterProvider>');
  }
  return value;
}
